import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Radio,
  RadioGroup,
  Spinner,
} from "@fluentui/react-components";
import { LinkRegular } from "@fluentui/react-icons";
import { AsyncSelect, ThemeProvider } from "@greco/components";
import Axios from "axios";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import debounce from "debounce-promise";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as API from "api/api";
import { useAppStoreState } from "store/AppStore";
import { useInsurerReportsStore } from "store/InsurerReportsStore";

type MatchKind = "paymentPlanEntry" | "payment";

// private persons have no clientName, their name comes from last + first name
const getClientDisplayName = (row: any): string =>
  row.clientName
    ? row.clientName
    : [row.clientLastName, row.clientFirstName].filter(Boolean).join(" ");

export const MatchInsurerReportEntryDialog = () => {
  const [
    {
      matchInsurerReportEntryDialog,
      matchInsurerReportEntryStatus,
      insurerReportEntries,
      insurerReports,
    },
    {
      setInsurerReportsStoreValue,
      setInsurerReportsStoreValues,
      matchInsurerReportEntry,
      applyInsurerReportEntry,
    },
  ] = useInsurerReportsStore();
  const { appState } = useAppStoreState();
  const { t } = useTranslation();

  const isLoading = matchInsurerReportEntryStatus === "loading";
  const entry = insurerReportEntries.find(
    (e) =>
      e.insurerReportEntryId ===
      matchInsurerReportEntryDialog.insurerReportEntryId
  );
  const report = insurerReports.find(
    (r) => r.insurerReportId === entry?.insurerReportId
  );

  const [matchKind, setMatchKind] = useState<MatchKind>("paymentPlanEntry");
  const [selectedTarget, setSelectedTarget] = useState<any>(null);

  useEffect(() => {
    if (matchInsurerReportEntryDialog.isOpen) {
      setMatchKind("paymentPlanEntry");
      setSelectedTarget(null);
    }
  }, [matchInsurerReportEntryDialog.isOpen]);

  const loadPaymentPlanEntryOptions = useMemo(
    () =>
      debounce(async (input: string) => {
        const term = input.trim();
        if (term.length === 0) return [];
        const columns: any[] = [];
        if (report) {
          // insurerId is a string field in the Azure Search index — filter via
          // search.in (taxonomy filter type), not a numeric eq comparison
          columns.push({
            key: "insurerId",
            filter: {
              type: "taxonomy",
              value: [report.insurerId],
            },
          });
          columns.push({
            key: "grecoCompanyNumber",
            filter: {
              taxonomyKey: "Company",
              type: "taxonomy",
              value: [report.grecoCompanyNumber],
            },
          });
        }
        const res = await API.paymentPlanEntry({
          search: term,
          sort: null,
          top: 20,
          skip: 0,
          cancelToken: Axios.CancelToken.source().token,
          columns,
        });
        return res.data.value.map((row) => ({
          value: row.paymentPlanEntryId,
          label:
            getClientDisplayName(row) +
            " | " +
            (row.policyNumber ?? "") +
            " | " +
            (row.premiumAmount ?? ""),
        }));
      }, 300),
    [report]
  );

  const loadPaymentOptions = useMemo(
    () =>
      debounce(async (input: string) => {
        const term = input.trim();
        if (term.length === 0) return [];
        const res = await API.paymentQuery({
          PolicyNumbers: [term],
          InsurerIds: report ? [report.insurerId] : [],
          GrecoCompanyNumbers: report ? [report.grecoCompanyNumber] : [],
        });
        return res.data.map((row) => ({
          value: row.paymentId,
          label:
            getClientDisplayName(row) +
            " | " +
            (row.policyNumber ?? "") +
            " | " +
            (row.premiumAmount ?? "") +
            (row.paymentDate
              ? " | " + moment(row.paymentDate).format("DD.MM.YYYY")
              : ""),
        }));
      }, 300),
    [report]
  );

  const closeDialog = () => {
    setInsurerReportsStoreValue("matchInsurerReportEntryDialog", {
      isOpen: false,
      insurerReportEntryId: null,
    });
  };

  return (
    <Dialog open={matchInsurerReportEntryDialog.isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("pct.matchInsurerReportEntry.label")}</DialogTitle>
          <DialogContent>
            <VSpace height={10} />
            {entry ? (
              <div>
                <b>{t("pct.client.label")}:</b> {entry.clientName} (
                {entry.clientCode})
              </div>
            ) : null}
            <VSpace height={15} />

            <Field label={t("pct.matchTargetType.label")}>
              <RadioGroup
                data-help="insurer-reports-match-type"
                value={matchKind}
                disabled={isLoading}
                onChange={(e, data) => {
                  setMatchKind(data.value as MatchKind);
                  setSelectedTarget(null);
                }}
              >
                <Radio
                  value="paymentPlanEntry"
                  label={t("pct.paymentPlanEntry.label")}
                />
                <Radio value="payment" label={t("pct.payment.label")} />
              </RadioGroup>
            </Field>
            <VSpace height={10} />

            <Field
              data-help="insurer-reports-match-target"
              label={
                matchKind === "payment"
                  ? t("pct.payment.label")
                  : t("pct.paymentPlanEntry.label")
              }
            >
              <ThemeProvider isDarkMode={appState.darkMode}>
                <AsyncSelect
                  id="matchTargetSearch"
                  key={matchKind}
                  loadOptions={
                    matchKind === "payment"
                      ? loadPaymentOptions
                      : loadPaymentPlanEntryOptions
                  }
                  clearable
                  isClearable
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  styles={{
                    // escape the DialogSurface overflow clipping; above Fluent v9 dialog layer
                    menuPortal: (base) => ({ ...base, zIndex: 2000000 }),
                  }}
                  placeholder={
                    matchKind === "payment"
                      ? t("pct.searchPaymentsByPolicyNumber.label")
                      : t("pct.searchPaymentPlanEntries.label")
                  }
                  noOptionsMessage={() => t("greco.noResults")}
                  loadingMessage={() => t("greco.searching")}
                  isDisabled={isLoading}
                  value={selectedTarget}
                  onChange={(value) => setSelectedTarget(value)}
                />
              </ThemeProvider>
            </Field>
            <VSpace height={10} />
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isLoading}
              onClick={closeDialog}
            >
              {t("pct.close.label")}
            </Button>
            <Button
              data-help="insurer-reports-match-submit"
              appearance="primary"
              disabled={isLoading || !selectedTarget}
              icon={
                isLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <LinkRegular fontSize={16} />
                )
              }
              onClick={() => {
                matchInsurerReportEntry(
                  {
                    insurerReportEntryId:
                      matchInsurerReportEntryDialog.insurerReportEntryId,
                    paymentId:
                      matchKind === "payment" ? selectedTarget.value : null,
                    paymentPlanEntryId:
                      matchKind === "paymentPlanEntry"
                        ? selectedTarget.value
                        : null,
                  },
                  (updatedEntry) => {
                    setInsurerReportsStoreValues({
                      matchInsurerReportEntryDialog: {
                        isOpen: false,
                        insurerReportEntryId: null,
                      },
                    });
                    toast.success(t("greco.success"));
                    applyInsurerReportEntry(updatedEntry);
                  }
                );
              }}
            >
              {t("pct.match.label")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
